import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IRootDispatch } from 'stores/rematch/root-store';
import { notification } from 'antd';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import _ from 'lodash';
import moment from 'moment-timezone';
import { BookingActionType, BookingType, EditRecurringMode, ShiftSlotStatus } from 'utilities/enum-utils';
import CommonUtils from 'utilities/common-utils';
import { IShiftClashConflict } from 'interfaces/service-interfaces';
import ConflictCheckUtils from 'utilities/conflict-check-utils';
import BookingActionSuccessNotificationContent from '../BookingActionSuccessNotificationContent';
import { RadioChangeEvent } from 'antd/lib/radio';
import { Address } from '@good/schemas';
import { IFilter } from 'interfaces/filter-interfaces';
import { BookingAssignWorkerActiveFilters, OnSubmitAssign, Worker } from './types';
import { ISession } from 'interfaces/session-interfaces';
import { AssignWorkerStep1 } from './assign-worker-step-1';
import { AssignWorkerStep2 } from './assign-worker-step-2';
import { AssignWorkerStep4 } from './assign-worker-step-4';
import { AssignWorkerStep5 } from './assign-worker-step-5';
import { AssignWorkerStep8 } from './assign-worker-step-8';
import { AssignWorkerStep6 } from './assign-worker-step-6';
import { AssignWorkerStep11 } from './assign-worker-step-11';
import { AssignWorkerStep12 } from './assign-worker-step-12';
import { AssignWorkerStep13 } from './assign-worker-step-13';
import { AssignWorkerStep14 } from './assign-worker-step-14';
import { AssignWorkerStep15 } from './assign-worker-step-15';
import { AssignWorkerStep16 } from './assign-worker-step-16';
import { AssignWorkerStep17 } from './assign-worker-step-17';

interface BookingAssignWorkerModalInnerProps {
  address?: Address;
  bookingId?: string;
  bookingRequestId?: string;
  bookingType?: BookingType;
  customerUserIds?: string[];
  displayTimezone?: string;
  endDateTime?: Date;
  isAssignedTeamMemberConfirmed?: boolean;
  isBookingCancelled?: boolean;
  isEditingBooking?: boolean;
  isRecurring?: boolean;
  noValidationStep?: boolean;
  onReset: (refreshData?: boolean) => void;
  onSubmitAssign: OnSubmitAssign;
  serviceId: string;
  sessions?: ISession[];
  startDateTime?: Date;
  stacked?: boolean;
}

export const BookingAssignWorkerModalInner = ({
  address,
  bookingId,
  bookingRequestId,
  bookingType,
  customerUserIds,
  displayTimezone,
  endDateTime,
  isAssignedTeamMemberConfirmed,
  isBookingCancelled,
  isEditingBooking,
  isRecurring,
  noValidationStep,
  onReset: onResetProp,
  onSubmitAssign: onSubmitAssignProp,
  serviceId,
  sessions,
  startDateTime,
  stacked = false,
}: BookingAssignWorkerModalInnerProps) => {
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [selectedOption, setSelectedOption] = useState<EditRecurringMode | null>(null);
  const { bookingsStore } = useDispatch<IRootDispatch>();
  const doCheckAssignWorker = bookingsStore.doCheckAssignWorker;
  const doCheckWorker = bookingsStore.doCheckWorker;
  const [isLoading, setIsLoading] = useState(false);
  const [scheduledRemoveConflict, setScheduledRemoveConflict] = useState<unknown | undefined>(undefined);
  const [shiftClashConflicts, setShiftClashConflicts] = useState<IShiftClashConflict[] | undefined>(undefined);
  const [shiftsOutsideGeneralAvailability, setShiftsOutsideGeneralAvailability] = useState<unknown[] | null>(null);
  const [isRemovePendingShiftSlots, setIsRemovePendingShiftSlots] = useState<boolean>(false);
  const [selectedShiftOutSideCanBeAssign, setSelectedShiftOutSideCanBeAssign] = useState<unknown[]>([]);
  const [workerTimesheetStartTime, setWorkerTimesheetStartTime] = useState<Date>(
    moment(moment.tz(startDateTime, displayTimezone).format('YYYY-MM-DD HH:mm')).toDate(),
  );
  const [workerTimesheetEndTime, setWorkerTimesheetEndTime] = useState<Date>(
    moment(moment.tz(endDateTime, displayTimezone).format('YYYY-MM-DD HH:mm')).toDate(),
  );
  const [conflictErrorMessage, setConflictErrorMessage] = useState<string | null>(null);
  const [ignoreShiftClashShiftIds, setIgnoreShiftClashShiftIds] = useState<string[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [conflicts, setConflicts] = useState<unknown | null>(null);
  const [addTimesheet, setAddTimesheet] = useState<boolean | null>(null);
  const [showUnavailableBookings, setShowUnavailableBookings] = useState<boolean>(false);
  const [listFilters, setListFilters] = useState<IFilter[]>([]);
  const [activeFilters, setActiveFilters] = useState<BookingAssignWorkerActiveFilters>({
    previouslyUsed: false,
    isSkillMatched: false,
    isAvailable: true,
    isCustomerPreferred: false,
  });
  const doFetchServiceRoster = bookingsStore.doFetchServiceRoster;
  const [filteredWorkers, setFilteredWorkers] = useState<Worker[]>([]);
  const [isLoadingFoundWorkers, setIsLoadingFoundWorkers] = useState<boolean>(false);
  const [shiftSlotStatus, setShiftSlotStatus] = useState<ShiftSlotStatus>(ShiftSlotStatus.CONFIRMED);
  const [step, setStep] = useState<number>(1);
  const isShiftStatusPending = shiftSlotStatus === ShiftSlotStatus.PENDING;

  const onReset = () => {
    const refreshData = step === 4 || step === 12 || step === 6;
    onResetProp?.(refreshData);
    setError(null);
    setStep(2);
    setConflicts(null);
    setActiveFilters({
      previouslyUsed: false,
      isSkillMatched: false,
      isAvailable: true,
      isCustomerPreferred: false,
    });
    setShiftSlotStatus(ShiftSlotStatus.CONFIRMED);
    setIsRemovePendingShiftSlots(false);
    setWorkerTimesheetStartTime(moment(moment.tz(startDateTime, displayTimezone).format('YYYY-MM-DD HH:mm')).toDate());
    setWorkerTimesheetEndTime(moment(moment.tz(endDateTime, displayTimezone).format('YYYY-MM-DD HH:mm')).toDate());
    setScheduledRemoveConflict(null);
    setShowUnavailableBookings(false);
    setActiveFilters({
      previouslyUsed: false,
      isSkillMatched: false,
      isAvailable: true,
      isCustomerPreferred: false,
    });
    setFilteredWorkers([]);
    setIsLoadingFoundWorkers(false);
    setListFilters([]);
    setShiftSlotStatus(ShiftSlotStatus.CONFIRMED);
    setStep(1);
  };

  const _formatFilterQuery = () => {
    const requestFilter: Record<string, unknown> = {};
    // biome-ignore lint/complexity/noForEach: <explanation>
    listFilters.forEach((filter) => {
      if (!_.isEmpty(filter.values)) {
        requestFilter[filter.filter] = filter.values;
      }
    });
    return requestFilter;
  };

  const _applyFilter = async () => {
    const { startDateTime, endDateTime } = _getCurrentBookingDateTime();
    setIsLoadingFoundWorkers(true);

    const requestFilters = _formatFilterQuery();
    const filteredWorkers = await doFetchServiceRoster({
      startDateTime,
      endDateTime,
      address,
      customerUserIds,
      serviceId,
      ...activeFilters,
      ...requestFilters,
    });
    setIsLoadingFoundWorkers(false);
    setFilteredWorkers(filteredWorkers);
  };

  const _getCurrentBookingDateTime = () => {
    const bookingStartDateTime = !_.isEmpty(sessions) ? sessions?.[0]?.startDateTime : startDateTime;
    const bookingEndDateTime = !_.isEmpty(sessions) ? sessions?.[0]?.endDateTime : endDateTime;

    return {
      startDateTime: bookingStartDateTime,
      endDateTime: bookingEndDateTime,
    };
  };

  useEffect(() => {
    setStep(1);
    (async () => {
      try {
        if (bookingType === BookingType.ACTIVITY_RECORD) {
          const result = await doFetchServiceRoster({
            address,
            customerUserIds,
            serviceId,
            ...activeFilters,
          });
          setStep(2);
          setFilteredWorkers(result);
        } else {
          const result = await doFetchServiceRoster({
            startDateTime,
            endDateTime,
            address,
            customerUserIds,
            serviceId,
            ...activeFilters,
          });
          setStep(2);
          setFilteredWorkers(result);
        }
      } catch (e) {
        setStep(5);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoadingFoundWorkers(true);
      if (bookingType === BookingType.ACTIVITY_RECORD) {
        const filteredWorkers = await doFetchServiceRoster({
          address,
          customerUserIds,
          serviceId,
          ...activeFilters,
        });
        setIsLoadingFoundWorkers(false);
        setFilteredWorkers(filteredWorkers);
      } else {
        const { startDateTime, endDateTime } = _getCurrentBookingDateTime();
        const filteredWorkers = await doFetchServiceRoster({
          startDateTime,
          endDateTime,
          address,
          customerUserIds,
          serviceId,
          ...activeFilters,
        });
        setIsLoadingFoundWorkers(false);
        setFilteredWorkers(filteredWorkers);
      }
    })();
  }, [activeFilters]);

  useEffect(() => {
    (async () => {
      await _applyFilter();
    })();
  }, [listFilters]);

  const _onSelectWorker = (selectedWorker: Worker | null) => {
    setSelectedWorker(selectedWorker);
  };

  const _onChangeOption = (event: RadioChangeEvent) => {
    setSelectedOption(+event.target.value as EditRecurringMode);
  };

  const _checkForConflict = async () => {
    if (!selectedWorker) {
      return;
    }

    setIsLoading(true);

    try {
      const result = isEditingBooking
        ? await doCheckAssignWorker({
            bookingRequestId,
            bookingId,
            editRecurringMode: selectedOption ? selectedOption : EditRecurringMode.Current,
            workerId: selectedWorker.supportWorkerId,
            startDateTime,
          })
        : bookingType === BookingType.BOOKING
          ? await doCheckWorker({
              serviceId: serviceId,
              workerId: selectedWorker.supportWorkerId,
              address: address ? { ...address, geoLat: Number(address.geoLat), geoLng: Number(address.geoLng) } : null,
              sessions: sessions,
              customerUserId: customerUserIds?.[0],
            })
          : { data: {} };

      const isSingleBooking = _checkIsSingleBooking();
      const removedData = result.data.sessionsAfterRemovedWorker || result.data.shiftsAfterRemovedWorker;
      const removedConflicts = removedData.sessions || removedData.shiftSlots || [];

      if (isRecurring && result && !_.isEmpty(removedConflicts)) {
        if (selectedOption === 1 && isEditingBooking) {
          setStep(15);
          setIsLoading(false);
          setScheduledRemoveConflict(removedData);
        } else {
          setStep(14);
          setIsLoading(false);
          setScheduledRemoveConflict(removedData);
        }
      } else if (result?.data && !_.isEmpty(result.data.shiftClashConflicts)) {
        setStep(17);
        setShiftClashConflicts(result.data.shiftClashConflicts);
        setShiftsOutsideGeneralAvailability(
          !_.isEmpty(result.data.shiftsOutsideGeneralAvailability)
            ? result.data.shiftsOutsideGeneralAvailability
            : null,
        );
        setIsLoading(false);
      } else if (
        result?.data &&
        !_.isEmpty(result.data.shiftsOutsideGeneralAvailability) &&
        !isSingleBooking &&
        isRecurring
      ) {
        setStep(16);
        setShiftsOutsideGeneralAvailability(result.data.shiftsOutsideGeneralAvailability);
        setIsLoading(false);
      } else {
        await _onSubmitAssign(shiftSlotStatus === ShiftSlotStatus.CONFIRMED, result.data.conflicts);
      }
    } catch (e) {
      setStep(5);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const _checkIsSingleBooking = () => {
    return isEditingBooking ? selectedOption === EditRecurringMode.Current || !isRecurring : !isRecurring;
  };

  const _onSubmitAssign = async (
    isRemovePendingShiftSlots = false,
    conflicts = null,
    ignoreShiftClashShiftIds: string[] = [],
  ) => {
    setIsLoading(true);
    setIsRemovePendingShiftSlots(!!isRemovePendingShiftSlots);
    const isSingleBooking = _checkIsSingleBooking();
    try {
      const bookingOutSideCanBeAssign = !_.isEmpty(shiftsOutsideGeneralAvailability)
        ? isSingleBooking
          ? [_.first(shiftsOutsideGeneralAvailability)]
          : selectedShiftOutSideCanBeAssign
        : [];

      const payload = {
        selectedWorker: selectedWorker,
        editRecurringMode: selectedOption ? selectedOption : EditRecurringMode.Current,
        shiftSlotStatus: shiftSlotStatus,
        isRemovePendingShiftSlots: isRemovePendingShiftSlots
          ? isRemovePendingShiftSlots
          : shiftSlotStatus === ShiftSlotStatus.CONFIRMED,
        startDateTime: moment.tz(moment(workerTimesheetStartTime).format('YYYY-MM-DD HH:mm'), displayTimezone),
        endDateTime: moment.tz(moment(workerTimesheetEndTime).format('YYYY-MM-DD HH:mm'), displayTimezone),
        conflicts,
        bookingOutSideCanBeAssign,
        bookingOutsideAvailability: shiftsOutsideGeneralAvailability,
        ignoreShiftClashShiftIds: _.isEmpty(ignoreShiftClashShiftIds)
          ? ignoreShiftClashShiftIds
          : ignoreShiftClashShiftIds,
      };

      let assignedShiftsCount = 0;
      let currentShiftStatus = ShiftSlotStatus.UNASSIGNED;

      if (isEditingBooking && bookingType !== BookingType.ACTIVITY_RECORD) {
        const result = await onSubmitAssignProp(payload);
        if (!result) {
          throw new Error('Failed to assign worker');
        }
        assignedShiftsCount = result.assignedShiftsCount;
        currentShiftStatus = result.currentShiftStatus;
      } else {
        await onSubmitAssignProp(payload);
      }

      if (assignedShiftsCount > 0) {
        notification.open({
          message: <b>Team member assigned</b>,
          description: (
            <BookingActionSuccessNotificationContent
              action={BookingActionType.ASSIGN}
              shiftsCount={assignedShiftsCount}
              currentShiftStatus={currentShiftStatus}
              workerName={`${selectedWorker?.firstName} ${selectedWorker?.lastName}`}
            />
          ),
        });
      }

      noValidationStep
        ? onReset()
        : bookingType === BookingType.BOOKING
          ? // ? this.setState({ step: 4 })
            onReset() // this will not display successful modal anymore, temporary solution
          : setStep(12);
    } catch (e: unknown) {
      console.log(e);
      let message = '';
      if (e instanceof Error) {
        message = e.message;
      }

      if ('meta' in (e as any) && (e as any).meta && 'message' in (e as any).meta) {
        message = (e as any).meta.message;
      }

      if (message === 'Cannot assign worker, worker conflicts exist') {
        setConflictErrorMessage('This Team member has scheduling conflicts and cannot be selected.');
        setStep(8);
      } else if (message.includes('Cannot assign worker,')) {
        setConflictErrorMessage(
          'This Team member is already assigned to a completed or in progress booking, please select another Team member.',
        );
        setStep(8);
      } else {
        setStep(5);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const _onAssignWorker = async (
    shiftSlotStatus?: ShiftSlotStatus,
    selectedIgnoreShiftClashShiftIds: string[] = [],
  ) => {
    let selectedWorker = { ...selectedWorker };
    let newShiftSlotStatus = ShiftSlotStatus.CONFIRMED;

    if (!_.isEmpty(shiftsOutsideGeneralAvailability) || !_.isEmpty(shiftClashConflicts)) {
      const allShiftIds = _.map(sessions, (session) => session.serviceDateTimeId);
      const outsideAvailabilityShiftIds = _.map(shiftsOutsideGeneralAvailability, (shift) => shift.serviceDateTimeId);
      const shiftClashShiftIds = !_.isEmpty(shiftClashConflicts)
        ? _.map(shiftClashConflicts[0].selectedShifts, (shift) => shift.serviceDateTimeId)
        : [];

      const ignoreOutsideAvailabilityShiftIds = _.map(
        selectedShiftOutSideCanBeAssign,
        (shift) => shift.serviceDateTimeId,
      );

      const conflictingShiftIds = _.union(outsideAvailabilityShiftIds, shiftClashShiftIds);
      const nonassignableShiftIds = ConflictCheckUtils.filterIgnoredConflictedShiftIds(
        conflictingShiftIds,
        _.isEmpty(selectedIgnoreShiftClashShiftIds) ? ignoreShiftClashShiftIds : selectedIgnoreShiftClashShiftIds,
        ignoreOutsideAvailabilityShiftIds,
        shiftClashShiftIds,
        outsideAvailabilityShiftIds,
      );

      const assignableShiftIds = allShiftIds;

      _.remove(assignableShiftIds, (serviceDateTimeId) => _.includes(nonassignableShiftIds, serviceDateTimeId));

      if (_.isEmpty(assignableShiftIds)) {
        newShiftSlotStatus = ShiftSlotStatus.UNASSIGNED;
        selectedWorker = null;
      }
    }

    setShiftSlotStatus(shiftSlotStatus || newShiftSlotStatus);

    if (isRecurring && isEditingBooking) {
      setStep(6);
    } else {
      await onSubmitAssignProp({
        selectedWorker,
        editRecurringMode: selectedOption ? selectedOption : EditRecurringMode.Current,
        shiftSlotStatus: shiftSlotStatus || newShiftSlotStatus,
        isRemovePendingShiftSlots: isRemovePendingShiftSlots
          ? isRemovePendingShiftSlots
          : shiftSlotStatus === ShiftSlotStatus.CONFIRMED,
        startDateTime: moment.tz(moment(workerTimesheetStartTime).format('YYYY-MM-DD HH:mm'), displayTimezone),
        endDateTime: moment.tz(moment(workerTimesheetEndTime).format('YYYY-MM-DD HH:mm'), displayTimezone),
        conflicts,
        bookingOutSideCanBeAssign: selectedShiftOutSideCanBeAssign,
        bookingOutsideAvailability: shiftsOutsideGeneralAvailability,
        shiftClashConflicts,
        ignoreShiftClashShiftIds: _.isEmpty(selectedIgnoreShiftClashShiftIds)
          ? ignoreShiftClashShiftIds
          : selectedIgnoreShiftClashShiftIds,
      });
      onReset();
    }
  };

  const _onClickAssign = (shiftSlotStatus: ShiftSlotStatus) => {
    setShiftSlotStatus(shiftSlotStatus);

    if (isRecurring && isEditingBooking) {
      setStep(6);
    } else {
      _checkForConflict();
    }
  };

  const _ChooseNewWorker = () => {
    setError(null);
    setSelectedWorker(null);
    setStep(2);
    setConflicts(null);
  };

  const _onChangeAddTimesheet = (e) => {
    setAddTimesheet(e.target.value);
  };

  const _onOpenAddTimesheet = () => {
    setStep(11);
  };

  const _onOpenEditTimesheet = () => {
    setStep(13);
    setAddTimesheet(null);
    setShiftSlotStatus(ShiftSlotStatus.CHECKED_OUT);
  };

  const _onChangeFilter = (filterLabel: string, newValue: any) => {
    setActiveFilters({ ...activeFilters, [filterLabel]: newValue });
  };

  const _onChangeActivityDate = (date: Date) => {
    const newStartDate = moment(date)
      .set('hour', moment(workerTimesheetStartTime).hour())
      .set('minute', moment(workerTimesheetStartTime).minute());

    const newEndDate = moment(date)
      .set('hour', moment(workerTimesheetEndTime).hour())
      .set('minute', moment(workerTimesheetEndTime).minute());

    setWorkerTimesheetStartTime(newStartDate.toDate());
    setWorkerTimesheetEndTime(newEndDate.toDate());
  };

  const _onChangeActivityStartTime = (date: Date) => {
    const newStartTime = CommonUtils.formatCeilingDateTime(moment(date));
    if (newStartTime.isAfter(moment(workerTimesheetEndTime))) {
      1;
      setWorkerTimesheetEndTime(moment(newStartTime).add(1, 'hour').toDate());
    }

    setWorkerTimesheetStartTime(newStartTime.toDate());
  };

  const _onChangeActivityEndTime = (date: Date) => {
    let newEndTime = CommonUtils.formatCeilingDateTime(moment(date));
    if (newEndTime.isBefore(moment(workerTimesheetStartTime))) {
      newEndTime = moment(workerTimesheetStartTime).add(1, 'hour');
    }
    setWorkerTimesheetEndTime(newEndTime.toDate());
  };

  const _showUnavailableBookings = () => {
    setShowUnavailableBookings(!showUnavailableBookings);
  };

  const _onChangeSelectedShiftToBeKept = (shifts: unknown[]) => {
    setSelectedShiftOutSideCanBeAssign(shifts);
  };

  const _onSubmitShiftClash = async (ignoreShiftClashShiftIds: string[]) => {
    const remainedShiftsOutsideGeneralAvailability =
      ConflictCheckUtils.filterOutsideAvailabilityShiftsAfterSelectingShiftClashShiftById(
        shiftClashConflicts,
        ignoreShiftClashShiftIds,
        shiftsOutsideGeneralAvailability,
      );

    if (!_.isEmpty(remainedShiftsOutsideGeneralAvailability) && !_checkIsSingleBooking()) {
      setStep(16);
      setIsLoading(false);
      setIgnoreShiftClashShiftIds(ignoreShiftClashShiftIds);
      setShiftsOutsideGeneralAvailability(remainedShiftsOutsideGeneralAvailability);
    } else {
      if (!isEditingBooking && isRecurring) {
        await _onAssignWorker(null, ignoreShiftClashShiftIds);
      } else {
        await _onSubmitAssign(null, null, ignoreShiftClashShiftIds);
      }
    }
  };

  const title =
    step === 14 || step === 16
      ? 'Team member unavailable for bookings'
      : step === 4 && shiftSlotStatus === ShiftSlotStatus.CONFIRMED
        ? 'Team member assigned and confirmed'
        : step === 4
          ? 'Team member assigned'
          : step === 7
            ? 'Team member assigned to conflicting shift(s)'
            : step === 6
              ? 'Select Workers'
              : step === 11
                ? 'Add hours to timesheet'
                : step === 12
                  ? 'Team member assigned'
                  : step === 13
                    ? 'Edit activity record'
                    : bookingType === BookingType.ACTIVITY_RECORD && step === 2
                      ? 'Select team member for this activity record'
                      : step === 17
                        ? 'Hold on, this team member is already working'
                        : 'Select team member for this booking';

  switch (step) {
    case 1:
      return <AssignWorkerStep1 />;

    case 2:
      return (
        <AssignWorkerStep2
          checkForConflict={_checkForConflict}
          onAssignWorker={_onAssignWorker}
          onChangeFilter={_onChangeFilter}
          onClickAssign={_onClickAssign}
          onOpenAddTimesheet={_onOpenAddTimesheet}
          onSelectWorker={_onSelectWorker}
          activeFilters={activeFilters}
          address={address}
          bookingId={bookingId}
          bookingType={bookingType}
          customerUserIds={customerUserIds}
          endDateTime={endDateTime}
          filteredWorkers={filteredWorkers}
          isAssignedTeamMemberConfirmed={isAssignedTeamMemberConfirmed}
          isBookingCancelled={isBookingCancelled}
          isEditingBooking={isEditingBooking}
          isLoadingFoundWorkers={isLoadingFoundWorkers}
          isRecurring={isRecurring}
          listFilters={listFilters}
          onChangeListFilter={setListFilters}
          selectedWorker={selectedWorker}
          serviceId={serviceId}
          startDateTime={startDateTime}
          stacked={stacked}
        />
      );

    case 3:
      return <SpinningLoader size={150} message={'Assigning worker ... '} />;

    case 4:
      return (
        <AssignWorkerStep4
          selectedWorker={selectedWorker}
          isShiftStatusPending={isShiftStatusPending}
          isRemovePendingShiftSlots={isRemovePendingShiftSlots}
          isRecurring={isRecurring}
          selectedOption={selectedOption}
          onClose={onReset}
        />
      );

    case 5:
      return <AssignWorkerStep5 />;

    case 6:
      return (
        <AssignWorkerStep6
          isLoading={isLoading}
          selectedOption={selectedOption}
          selectedWorker={selectedWorker}
          onChangeOption={_onChangeOption}
          onClose={onReset}
          checkForConflict={_checkForConflict}
        />
      );

    case 8:
      return (
        <AssignWorkerStep8
          conflictErrorMessage={conflictErrorMessage}
          onClose={onReset}
          chooseNewWorker={_ChooseNewWorker}
        />
      );

    case 11:
      return (
        <AssignWorkerStep11
          addTimesheet={addTimesheet}
          isLoading={isLoading}
          onChangeAddTimesheet={_onChangeAddTimesheet}
          onClose={onReset}
          onOpenEditTimesheet={_onOpenEditTimesheet}
          onSubmitAssign={_onSubmitAssign}
        />
      );

    case 12:
      return (
        <AssignWorkerStep12
          selectedWorker={selectedWorker}
          bookingType={bookingType}
          shiftSlotStatus={shiftSlotStatus}
        />
      );

    case 13:
      return (
        <AssignWorkerStep13
          workerTimesheetStartTime={workerTimesheetStartTime}
          workerTimesheetEndTime={workerTimesheetEndTime}
          displayTimezone={displayTimezone}
          isLoading={isLoading}
          onChangeActivityDate={_onChangeActivityDate}
          onChangeActivityStartTime={_onChangeActivityStartTime}
          onChangeActivityEndTime={_onChangeActivityEndTime}
          onClose={onReset}
          onSubmitAssign={_onSubmitAssign}
        />
      );

    case 14:
      return (
        <AssignWorkerStep14
          scheduledRemoveConflict={scheduledRemoveConflict}
          selectedWorker={selectedWorker}
          displayTimezone={displayTimezone}
          isShiftStatusPending={isShiftStatusPending}
          showUnavailableBookings={showUnavailableBookings}
          _showUnavailableBookings={_showUnavailableBookings}
          onClose={onReset}
          onSubmitAssign={_onSubmitAssign}
        />
      );

    case 15:
      return (
        <AssignWorkerStep15
          displayTimezone={displayTimezone}
          onClose={onReset}
          scheduledRemoveConflict={scheduledRemoveConflict}
          selectedWorker={selectedWorker}
        />
      );

    case 16:
      return (
        <AssignWorkerStep16
          displayTimezone={displayTimezone}
          isEditingBooking={isEditingBooking}
          isRecurring={isRecurring}
          onAssignWorker={_onAssignWorker}
          onChangeSelectedShiftToBeKept={_onChangeSelectedShiftToBeKept}
          onClose={onReset}
          onSubmitAssign={_onSubmitAssign}
          shiftSlotStatus={shiftSlotStatus}
          shiftsOutsideGeneralAvailability={shiftsOutsideGeneralAvailability}
        />
      );

    case 17:
      return (
        <AssignWorkerStep17
          shiftClashConflicts={shiftClashConflicts}
          isRecurring={isRecurring}
          selectedOption={selectedOption}
          isLoading={isLoading}
          onCancel={() => {
            setError(null);
            setSelectedWorker(null);
            setStep(2);
          }}
          onSubmitShiftClash={_onSubmitShiftClash}
        />
      );
  }

  return null;
};
